import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import {
  KycTokenSubmissionService,
  KycTokenVerifyResponse,
} from 'src/app/shared/services/kyc-token-submission.service';
import { KycTokenAuthService } from 'src/app/shared/services/kyc-token-auth.service';
import {
  RfqKycRequest,
  RfqKycRequestDocument,
} from 'src/app/shared/services/rfq-kyc-request.service';
import { AwsSdkService } from 'src/app/shared/services/aws-sdk.service';
import { KycTokenSlotVm } from '../kyc-token-slot/kyc-token-slot.component';

// =========================================================================
// Page-level state machine — drives which top-level block the template
// renders. Mirrors the textual diagram in the saved plan.
// =========================================================================
type KycTokenPageState =
  | 'VERIFYING'   // initial; verify-kyc-token in flight
  | 'LOADED'      // bundle + documents ready, form shown
  | 'SUBMITTING'  // flushing pending picks then /submit
  | 'SUBMITTED'   // terminal success
  | 'ERROR';      // terminal error (invalid / expired / network)

type KycTokenErrorReason = 'INVALID_TOKEN' | 'EXPIRED_TOKEN' | 'NETWORK';

const MAX_FILE_BYTES = 10 * 1024 * 1024;        // 10 MB per slot
const ALLOWED_MIME = new Set<string>([
  'application/pdf',
  'image/jpeg',
  'image/png',
]);

// Top-level S3 folder for KYC token uploads. Kept in sync with the
// existing supplier KYC submit dialog convention (one folder per
// flow) so the buyer-side viewer code that recognises the prefix can
// keep working unchanged.
const S3_FOLDER = 'kyc-token-submissions';

@Component({
  selector: 'app-kyc-token-submit-page',
  templateUrl: './kyc-token-submit-page.component.html',
  styleUrls: ['./kyc-token-submit-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KycTokenSubmitPageComponent implements OnInit, OnDestroy {

  // ----------------- Page state ----------------------------------------
  pageState: KycTokenPageState = 'VERIFYING';
  errorReason: KycTokenErrorReason | null = null;

  // ----------------- Verified context ----------------------------------
  supplierFirstName = '';
  supplierLastName = '';
  buyerCompanyName = '';
  requestMessage: string | null = null;

  // RFQ context — populated only when the bundle is RFQ-linked
  // (rfqId !== null). The page surfaces this as a small chip strip so
  // the supplier sees which RFQ the KYC pertains to before they upload.
  rfqId: number | null = null;
  rfqRefNumber: string | null = null;
  vesselName: string | null = null;

  // ----------------- Slot view-models ---------------------------------
  slots: KycTokenSlotVm[] = [];

  // ----------------- Drag-drop overlay flag ---------------------------
  // Goes true when the user starts dragging a file anywhere on the
  // page; the template renders a translucent overlay that tells them
  // to drop the file on a specific document slot.
  isDraggingFile = false;
  private dragEnterDepth = 0;

  // ----------------- Animated documents-ready counter ----------------
  // Tweens between values so the stat block feels alive when a slot
  // flips between EMPTY / PENDING / UPLOADED.
  displayedFilledCount = 0;
  private counterRaf: number | null = null;

  private bundle: RfqKycRequest | null = null;
  /** 2026-10-26 (G1 review L6): the hero shows "Verified Supplier" only once this KYC request was ACCEPTED. */
  get kycRequestAccepted(): boolean {
    return this.bundle?.status === 'ACCEPTED';
  }
  private rawToken: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private snack: MatSnackBar,
    private tokenAuth: KycTokenAuthService,
    private tokenApi: KycTokenSubmissionService,
    private awsSdk: AwsSdkService,
    private cdr: ChangeDetectorRef
  ) {}

  // =====================================================================
  // Lifecycle
  // =====================================================================
  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const token = (params.get('token') || '').trim();
        this.rawToken = token || null;
        if (!token) {
          this.failWith('INVALID_TOKEN');
          return;
        }
        this.runVerifyAndLoad(token);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.counterRaf !== null) {
      cancelAnimationFrame(this.counterRaf);
      this.counterRaf = null;
    }
    // Deliberate: token is cleared on destroy so a closed tab can't be
    // reopened with an in-memory session intact.
    this.tokenAuth.clear();
  }

  // ====================================================================
  // Page-level drag-and-drop detection.
  //
  // We track dragenter / dragleave with a depth counter because the
  // browser fires those events for every child node — a naive boolean
  // would flicker every time the cursor crosses an internal element.
  // ====================================================================
  @HostListener('dragenter', ['$event'])
  onPageDragEnter(event: DragEvent): void {
    // Only react to real file drags (ignore HTML5 internal drags).
    if (!event.dataTransfer || !event.dataTransfer.types?.includes('Files')) {
      return;
    }
    event.preventDefault();
    this.dragEnterDepth++;
    if (!this.isDraggingFile) {
      this.isDraggingFile = true;
      this.cdr.markForCheck();
    }
  }

  @HostListener('dragover', ['$event'])
  onPageDragOver(event: DragEvent): void {
    // preventDefault is required for drop to fire on child targets.
    if (event.dataTransfer && event.dataTransfer.types?.includes('Files')) {
      event.preventDefault();
    }
  }

  @HostListener('dragleave', ['$event'])
  onPageDragLeave(event: DragEvent): void {
    if (!event.dataTransfer || !event.dataTransfer.types?.includes('Files')) {
      return;
    }
    this.dragEnterDepth = Math.max(0, this.dragEnterDepth - 1);
    if (this.dragEnterDepth === 0 && this.isDraggingFile) {
      this.isDraggingFile = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('drop', ['$event'])
  onPageDrop(event: DragEvent): void {
    // A drop that bubbled all the way up to the host means the file
    // missed every slot. Reset the overlay and let the slot-level
    // handlers do the real work where they fire first.
    this.dragEnterDepth = 0;
    if (this.isDraggingFile) {
      this.isDraggingFile = false;
      this.cdr.markForCheck();
    }
    if (event.dataTransfer && event.dataTransfer.types?.includes('Files')) {
      event.preventDefault();
    }
  }

  // ====================================================================
  // Spotlight hover — set CSS variables on the slot wrap so a radial
  // gradient overlay tracks the cursor. The overlay is painted in CSS.
  // ====================================================================
  onSlotMouseMove(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty('--ktp-mx', `${x}%`);
    el.style.setProperty('--ktp-my', `${y}%`);
  }

  // ====================================================================
  // Animated documents-ready counter. Eases the displayed value
  // toward the target over ~280ms using requestAnimationFrame so the
  // big "0 / 3" stat block feels responsive without ever lying about
  // the actual state.
  // ====================================================================
  private tweenFilledCounter(to: number): void {
    if (this.counterRaf !== null) {
      cancelAnimationFrame(this.counterRaf);
      this.counterRaf = null;
    }
    const from = this.displayedFilledCount;
    if (from === to) return;
    const start = performance.now();
    const duration = 280;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out-cubic — fast start, soft landing
      const eased = 1 - Math.pow(1 - t, 3);
      const value = from + (to - from) * eased;
      // Round so we display whole document counts during the tween.
      this.displayedFilledCount = Math.round(value);
      this.cdr.markForCheck();
      if (t < 1) {
        this.counterRaf = requestAnimationFrame(tick);
      } else {
        this.counterRaf = null;
        this.displayedFilledCount = to;
        this.cdr.markForCheck();
      }
    };
    this.counterRaf = requestAnimationFrame(tick);
  }

  /** Recompute the filled count from the slots and start the tween. */
  private refreshFilledCounter(): void {
    this.tweenFilledCounter(this.filledCount());
  }

  // =====================================================================
  // 1. VERIFY → 2. GET BY TOKEN
  // =====================================================================
  private async runVerifyAndLoad(token: string): Promise<void> {
    try {
      const verify: KycTokenVerifyResponse = await this.tokenApi.verify(token);

      if (!verify.valid) {
        this.failWith(
          verify.reason === 'EXPIRED_TOKEN' ? 'EXPIRED_TOKEN' : 'INVALID_TOKEN'
        );
        return;
      }

      this.tokenAuth.set({
        token,
        kycRequestId: verify.kycRequestId!,
        buyerId: verify.buyerId!,
        supplierId: verify.supplierId!,
      });

      this.supplierFirstName = verify.supplierFirstName || 'there';
      this.supplierLastName = verify.supplierLastName || '';
      this.buyerCompanyName =
        verify.buyerCompanyName ||
        [verify.buyerFirstName, verify.buyerLastName].filter(Boolean).join(' ') ||
        'the buyer';

      const bundle = await this.tokenApi.getByToken(token);
      this.bundle = bundle;
      this.requestMessage = bundle.requestMessage;
      this.rfqId = bundle.rfqId ?? null;
      this.rfqRefNumber = bundle.rfqRefNumber ?? null;
      this.vesselName = bundle.vesselName ?? null;

      // Second-visit short-circuit: if the bundle is already past
      // PENDING_UPLOAD, drop straight to the success card so the
      // supplier can't accidentally resubmit.
      if (
        bundle.status === 'UPLOADED' ||
        bundle.status === 'ACCEPTED' ||
        bundle.status === 'REJECTED'
      ) {
        this.pageState = 'SUBMITTED';
        this.cdr.markForCheck();
        return;
      }

      this.slots = (bundle.documents || [])
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((doc) => this.makeSlotVm(doc));

      this.pageState = 'LOADED';
      // Initial counter — start at zero and tween up to whatever was
      // already uploaded before the supplier reopened the link.
      this.displayedFilledCount = 0;
      this.refreshFilledCounter();
      this.cdr.markForCheck();
    } catch (err) {
      console.error('[KYC-TOKEN-PAGE] verify/load failed', err);
      this.failWith('NETWORK');
    }
  }

  private makeSlotVm(doc: RfqKycRequestDocument): KycTokenSlotVm {
    return {
      doc,
      pendingFile: null,
      state: doc.uploadedFileUrl ? 'UPLOADED' : 'EMPTY',
      errorMessage: null,
    };
  }

  private failWith(reason: KycTokenErrorReason): void {
    this.errorReason = reason;
    this.pageState = 'ERROR';
    this.cdr.markForCheck();
  }

  // =====================================================================
  // 3. Slot interactions — events from KycTokenSlotComponent
  // =====================================================================
  onSlotPick(slot: KycTokenSlotVm, file: File): void {
    // Validate locally so the supplier gets immediate feedback.
    if (file.size > MAX_FILE_BYTES) {
      slot.errorMessage = 'File is larger than 10 MB. Please choose a smaller file.';
      this.cdr.markForCheck();
      return;
    }
    if (!ALLOWED_MIME.has(file.type)) {
      slot.errorMessage = 'Only PDF, JPG or PNG files are accepted.';
      this.cdr.markForCheck();
      return;
    }

    slot.pendingFile = file;
    slot.errorMessage = null;

    // EMPTY → PENDING, UPLOADED → REPLACING (keeps the old URL visible
    // until the new PUT succeeds), PENDING → PENDING (just swap the file).
    if (slot.state === 'UPLOADED') {
      slot.state = 'REPLACING';
      void this.flushSlot(slot);
    } else {
      slot.state = 'PENDING';
    }
    this.refreshFilledCounter();
    this.cdr.markForCheck();
  }

  onSlotRemove(slot: KycTokenSlotVm): void {
    slot.pendingFile = null;
    slot.errorMessage = null;
    // If the slot already had a saved URL we leave that alone — Remove
    // on an UPLOADED slot would require a backend delete endpoint we
    // don't have yet. Remove only clears the *pending pick*.
    if (slot.state === 'PENDING') {
      slot.state = 'EMPTY';
    }
    this.refreshFilledCounter();
    this.cdr.markForCheck();
  }

  onSlotReplaceRequested(slot: KycTokenSlotVm): void {
    // No-op at the page level; the slot template already triggers its
    // own hidden input on the Replace label click. Kept here as an
    // event hook in case we later want to log Replace usage.
  }

  onSlotPreviewRequested(slot: KycTokenSlotVm): void {
    if (!slot.doc.uploadedFileUrl) return;
    window.open(slot.doc.uploadedFileUrl, '_blank', 'noopener');
  }

  onSlotRetryRequested(slot: KycTokenSlotVm): void {
    if (!slot.pendingFile) return;
    slot.errorMessage = null;
    void this.flushSlot(slot);
  }

  // =====================================================================
  // 4. Upload flush — moves a slot from PENDING → UPLOADED via S3
  // =====================================================================
  private async flushSlot(slot: KycTokenSlotVm): Promise<boolean> {
    if (!slot.pendingFile || !this.rawToken) return false;
    const file = slot.pendingFile;
    const previousState = slot.state;
    slot.state =
      previousState === 'REPLACING' ? 'REPLACING' : 'UPLOADING';
    this.cdr.markForCheck();

    try {
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
      const fileKey = `${S3_FOLDER}/${this.tokenAuth.getKycRequestId()}-${slot.doc.id}-${uuidv4()}.${ext}`;

      // 4a — presigned PUT URL handout.
      const presigned: string = await this.awsSdk.getSignedUrlPromise({
        fileKey,
        fileContentType: file.type,
      });

      // 4b — direct PUT to S3 from the browser.
      const putRes = await fetch(presigned, {
        method: 'PUT',
        // 2026-10-25 (S3 step 2): kyc-token-submissions/ is private, so the URL is signed without an ACL; sending
        // x-amz-acl now would fail the signature check.
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 PUT failed with status ${putRes.status}`);
      }

      // 4c — strip the signature → that's the canonical viewable URL.
      const publicUrl = this.awsSdk.extractS3LocationFromUrl(presigned);

      // 4d — record metadata on the bundle.
      const updatedDoc = await this.tokenApi.upload({
        token: this.rawToken,
        documentId: slot.doc.id,
        fileUrl: publicUrl,
        fileName: file.name,
        fileSizeBytes: file.size,
        fileMimeType: file.type,
      });

      // Merge backend echo into the slot.
      slot.doc.uploadedFileUrl = updatedDoc.uploadedFileUrl;
      slot.doc.uploadedFileName = updatedDoc.uploadedFileName;
      slot.doc.uploadedFileSizeBytes = updatedDoc.uploadedFileSizeBytes;
      slot.doc.uploadedFileMimeType = updatedDoc.uploadedFileMimeType;
      slot.doc.uploadedAt = updatedDoc.uploadedAt;
      slot.pendingFile = null;
      slot.state = 'UPLOADED';
      slot.errorMessage = null;
      this.cdr.markForCheck();
      return true;
    } catch (err: any) {
      console.error('[KYC-TOKEN-PAGE] flushSlot failed', err);
      // Revert UPLOADING → PENDING so the supplier can retry without
      // losing their pick. REPLACING → PENDING also lets them retry
      // without losing the prior URL because we never cleared it.
      slot.state = 'PENDING';
      slot.errorMessage =
        'Upload failed. Please check your connection and click Retry.';
      this.cdr.markForCheck();
      return false;
    }
  }

  // =====================================================================
  // 5. Submit — flush every pending slot, then call /submit
  // =====================================================================
  async onSubmit(): Promise<void> {
    if (!this.canSubmit() || !this.rawToken) return;

    this.pageState = 'SUBMITTING';
    this.cdr.markForCheck();

    // Flush any remaining PENDING slots first. UPLOADED slots are
    // skipped naturally. If any flush fails we drop back to LOADED
    // so the supplier can retry the failed slot.
    for (const slot of this.slots) {
      if (slot.state === 'PENDING') {
        const ok = await this.flushSlot(slot);
        if (!ok) {
          this.pageState = 'LOADED';
          this.snack.open(
            'Some files failed to upload. Please retry and submit again.',
            'OK',
            { duration: 4000 }
          );
          this.cdr.markForCheck();
          return;
        }
      }
    }

    try {
      const finalBundle = await this.tokenApi.submit(this.rawToken);
      this.bundle = finalBundle;
      this.pageState = 'SUBMITTED';
      this.cdr.markForCheck();
    } catch (err) {
      console.error('[KYC-TOKEN-PAGE] submit failed', err);
      this.pageState = 'LOADED';
      this.snack.open(
        'We could not finalise the submission. Please try again in a moment.',
        'OK',
        { duration: 4000 }
      );
      this.cdr.markForCheck();
    }
  }

  // =====================================================================
  // Derived view helpers
  // =====================================================================
  canSubmit(): boolean {
    if (this.pageState !== 'LOADED') return false;
    if (this.slots.length === 0) return false;
    return this.slots.every(
      (s) => s.state === 'UPLOADED' || s.state === 'PENDING'
    );
  }

  filledCount(): number {
    return this.slots.filter(
      (s) => s.state === 'UPLOADED' || s.state === 'PENDING'
    ).length;
  }

  trackBySlot(_idx: number, slot: KycTokenSlotVm): number {
    return slot.doc.id;
  }

  // Error-card copy — kept in one place so the template stays declarative.
  errorTitle(): string {
    switch (this.errorReason) {
      case 'EXPIRED_TOKEN':
        return 'This link has expired';
      case 'INVALID_TOKEN':
        return 'This link is no longer valid';
      default:
        return 'Something went wrong';
    }
  }

  errorBody(): string {
    switch (this.errorReason) {
      case 'EXPIRED_TOKEN':
        return 'KYC invitation links are valid for 30 days. Please contact the buyer to request a fresh invitation.';
      case 'INVALID_TOKEN':
        return 'We could not find a matching KYC request for this link. Please double-check the URL or contact the buyer for a new invitation.';
      default:
        return 'We could not reach our servers. Please check your internet connection and refresh the page.';
    }
  }
}
